// EditUserGroupDrawer.tsx
import React, { useEffect } from "react";
import { Drawer, Form, Input, Select, Button, Space, message } from "antd";
import { UIAssetFilterDTO, UserDTO, UserGroupCreateDTO, UserGroupDTO, UserViewDTO } from "../../backendapi/requests";
import { useUserGroupServicePutBackendv2Usergroup } from "../../backendapi/queries";

const { Option } = Select;

interface EditUserGroupDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userGroup: UserGroupDTO;
  availableUsers: UserViewDTO[];
  availableFilters: UIAssetFilterDTO[];
}

interface UserGroupFormData {
  name: string;
  clientId: number;
  users: number[];
  filters: number[];
}

const EditUserGroupDrawer: React.FC<EditUserGroupDrawerProps> = ({ visible, onClose, onSuccess, userGroup, availableUsers, availableFilters }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const { mutateAsync: updateUserGroup } = useUserGroupServicePutBackendv2Usergroup();

  useEffect(() => {
    if (visible && userGroup) {
      form.setFieldsValue({
        name: userGroup.name,
        users: userGroup.users.map((x) => x.id),
        filters: userGroup.filters.map((x) => x.id),
      });
    }
  }, [visible, userGroup, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue(true) as UserGroupFormData;

      const formData = {
        clientId: userGroup.id,
        name: values.name,
        filters: availableFilters.filter((f) => values.filters.includes(f.id)),
        users: availableUsers.filter((f) => values.users.includes(f.id)),
      } as UserGroupCreateDTO;

      await updateUserGroup({ key: userGroup.id, requestBody: formData });

      message.success("User group updated successfully");
      onSuccess();
      onClose();
    } catch (error) {
      message.error("Failed to update user group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Edit User Group"
      placement="right"
      size={500}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            Update
          </Button>
        </Space>
      }>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: "Please enter a name" },
            { min: 3, message: "Name must be at least 3 characters" },
          ]}>
          <Input placeholder="Enter user group name" />
        </Form.Item>

        <Form.Item label="Users" name="users">
          <Select mode="multiple" placeholder="Select users" options={availableUsers.map((x) => ({ label: x.username, value: x.id }))} showSearch />
        </Form.Item>

        <Form.Item label="Filters" name="filters">
          <Select mode="multiple" placeholder="Select filters" options={availableFilters.map((x) => ({ label: x.title, value: x.id }))} showSearch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditUserGroupDrawer;
